import { useState, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";

import { getLibraryItem, LibraryError, type LibraryItemDetail } from "@/lib/api/library";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/library/$id")({
  component: LibraryDetailPage,
});

function LibraryDetailPage() {
  const { id } = Route.useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // Defer the anonymous redirect until auth has finished initialising.
  // Without this guard, a reload with a valid server session but no
  // localStorage hint would redirect to /login before /v1/auth/me returns.
  if (loading) return <main aria-busy>Loading…</main>;

  if (!user) {
    void navigate({ to: "/login", search: { next: `/library/${id}` } });
    return null;
  }

  return <AuthedLibraryDetail id={id} />;
}

interface BodyResult {
  url: string;
  text: string | null;
  error: string | null;
}

function AuthedLibraryDetail({ id }: { id: string }) {
  const navigate = useNavigate();
  const [bodyResult, setBodyResult] = useState<BodyResult | null>(null);

  const detail = useQuery<LibraryItemDetail, LibraryError>({
    queryKey: ["library-item", id],
    queryFn: () => getLibraryItem(id),
    retry: (failureCount, err) =>
      err instanceof LibraryError &&
      err.kind !== "unauthorized" &&
      err.kind !== "not_found" &&
      failureCount < 3,
  });

  const downloadUrl = detail.data?.download_url;
  const takenDown = detail.data?.taken_down ?? false;

  // Fetch body from signed URL; track which URL the result came from so stale
  // results from a previous URL don't show after a detail refresh.
  useEffect(() => {
    if (!downloadUrl) return;
    const url = downloadUrl;
    let cancelled = false;
    const controller = new AbortController();
    fetch(url, { signal: controller.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`status ${r.status}`);
        return r.text();
      })
      .then((text) => {
        if (!cancelled) setBodyResult({ url, text, error: null });
      })
      .catch(() => {
        if (cancelled) return;
        setBodyResult({
          url,
          text: null,
          error: takenDown
            ? "This artifact was taken down and is no longer available for download."
            : "Couldn't load the artifact body. Try refreshing — the link may have expired.",
        });
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [downloadUrl, takenDown]);

  useEffect(() => {
    if (
      detail.isError &&
      detail.error instanceof LibraryError &&
      detail.error.kind === "unauthorized"
    ) {
      void navigate({ to: "/login" });
    }
  }, [detail.isError, detail.error, navigate]);

  // Derive display body/error: only show result if it matches the current URL.
  const body = bodyResult !== null && bodyResult.url === downloadUrl ? bodyResult.text : null;
  const bodyError = bodyResult !== null && bodyResult.url === downloadUrl ? bodyResult.error : null;

  if (detail.isPending) return <main aria-busy>Loading…</main>;
  if (detail.isError) {
    if (detail.error instanceof LibraryError && detail.error.kind === "not_found") {
      return (
        <main role="alert" className="library-detail-error">
          <h1>Not in your library</h1>
          <p>
            You don&apos;t own this artifact. <a href="/marketplace">Browse the marketplace</a>.
          </p>
        </main>
      );
    }
    return <main role="alert">Couldn&apos;t load this artifact.</main>;
  }

  const a = detail.data;

  return (
    <main className="library-detail">
      <header>
        <h1>{a.title}</h1>
        <p className="summary">{a.summary}</p>
        <div className="meta">
          <span className="seller">
            by {a.automaton_name}
            {a.automaton_status === "dead" && (
              <span className="badge badge-stopped"> · stopped</span>
            )}
          </span>
          {a.taken_down && <span className="badge badge-takendown">taken down</span>}
          <time dateTime={a.purchased_at}>purchased {a.purchased_at.split("T")[0]}</time>
        </div>
      </header>

      <section className="description">
        <h2>About</h2>
        <p>{a.description}</p>
      </section>

      <section className="body">
        <header className="body-toolbar">
          <h2>Body</h2>
          {a.download_url && (
            <a
              className="btn btn-secondary"
              href={a.download_url}
              download={`${slugify(a.title)}.md`}
            >
              Download
            </a>
          )}
        </header>

        {body === null && !bodyError && <p aria-busy>Loading body…</p>}
        {bodyError && (
          <p role="alert" className="error">
            {bodyError}
          </p>
        )}
        {body && (
          <article className="markdown-body">
            <ReactMarkdown>{body}</ReactMarkdown>
          </article>
        )}
      </section>
    </main>
  );
}

function slugify(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "artifact"
  );
}
